import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useApi } from '../../api/useApi.hook';
import { AiFeatureType } from '../types/credit.types';

/**
 * Hook for managing credit balance and checking if user has sufficient credits
 */
export function useCredits() {
    const api = useApi();

    // Fetch wallet balance from API
    const { data: balance, isLoading: isLoadingBalance, error: balanceError } = useQuery({
        queryKey: ['wallet-balance'],
        queryFn: () => api.v1.credits.getBalance(),
        refetchOnWindowFocus: true,
    });

    // Fetch AI feature costs from API
    const { data: featureCosts, isLoading: isLoadingCosts } = useQuery({
        queryKey: ['feature-costs'],
        queryFn: () => api.v1.credits.getFeatureCosts(),
        staleTime: 300000, // Consider data fresh for 5 minutes (costs don't change often)
    });

    // Create a map of feature costs for quick lookup
    const featureCostMap = useMemo(() => {
        if (!featureCosts) return null;

        // TODO: Map backend AiFeatureType enum values to frontend enum values
        // Backend uses camelCase (e.g., 'languageCorrection')
        // Frontend uses snake_case (e.g., 'language_correction')
        const costMap: Record<string, number> = {};
        featureCosts.forEach((cost) => {
            // Map backend enum to frontend enum
            const frontendKey = String(cost.featureType).replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
            costMap[frontendKey] = cost.costPerUnit;
        });
        return costMap;
    }, [featureCosts]);

    /**
     * Check if user has sufficient credits for an AI feature
     *
     * @param requiredCredits - Number of credits required
     * @returns true if user has enough credits, false otherwise
     */
    const hasEnoughCredits = useCallback((requiredCredits: number): boolean => {
        if (!balance) return false;
        return balance >= requiredCredits;
    }, [balance]);

    /**
     * Get the cost of an AI feature
     *
     * @param featureType - Type of AI feature
     * @returns Number of credits required for the feature
     */
    const getFeatureCost = useCallback((featureType: AiFeatureType): number => {
        if (!featureCostMap) {
            // Fallback to hardcoded costs if API hasn't loaded yet
            const fallbackCosts: Record<AiFeatureType, number> = {
                [AiFeatureType.LANGUAGE_CORRECTION]: 1,
                [AiFeatureType.PAGE_CORRECTION]: 5,
                [AiFeatureType.PAGE_CORRECTION_BASIC]: 5,
                [AiFeatureType.PAGE_CORRECTION_ADVANCED]: 8,
                [AiFeatureType.CITATION_GENERATION]: 2,
                [AiFeatureType.DOCUMENT_UPLOAD]: 3,
            };
            return fallbackCosts[featureType] || 1;
        }

        return featureCostMap[featureType] || 1;
    }, [featureCostMap]);

    /**
     * Check if user can use a specific AI feature
     *
     * @param featureType - Type of AI feature
     * @param quantity - Quantity of the feature (e.g., number of pages, citations)
     * @returns Object with canUse flag and required credits
     */
    const canUseFeature = useCallback((featureType: AiFeatureType, quantity: number = 1) => {
        const costPerUnit = getFeatureCost(featureType);
        const requiredCredits = costPerUnit * quantity;
        const canUse = hasEnoughCredits(requiredCredits);

        return {
            canUse,
            requiredCredits,
            currentBalance: balance ?? 0,
        };
    }, [balance, getFeatureCost, hasEnoughCredits]);

    return {
        balance: balance ?? 0,
        isLoading: isLoadingBalance || isLoadingCosts,
        error: balanceError,
        hasEnoughCredits,
        getFeatureCost,
        canUseFeature,
    };
}

